import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from "../../../messages/en-US";
import moment from "moment";
import { SortDescriptor, process } from "@progress/kendo-data-query";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { Grid, GridColumn as Column, GridToolbar} from "@progress/kendo-react-grid";
import { CustomTextFilterCell } from "../../Shared/Components/Table/CustomTextFilterCell";
import { Button } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { downloadIcon } from "@progress/kendo-svg-icons";
interface Props {
  events: any;
}

interface Page {
  skip: number;
  take: number;
}

const page: Page = { skip: 0, take: 30 };

export default function FlowRateEventTable(props: Props): ReactElement {
  const localizationService = useLocalization();
  const initialSort: Array<SortDescriptor> = [
    { field: "timestamp", dir: "desc" },
  ];
  const [sort] = React.useState(initialSort);
  const _export = React.useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

   //Creating dataState to prevent issues with virtual scrolling
   const createAppState = (dataState) => {
    return {
      result: process(props.events, dataState),
      dataState: dataState,
    };
  };

  const [dataState, setDataState] = useState<any>(
    createAppState({
      skip: page.skip,
      take: page.take,
      sort: sort
    })
  );
 
  const dataStateChange = (event) => {
    setDataState(createAppState(event.dataState));
  };

  useEffect(() => {
    setDataState( createAppState({
      skip: page.skip,
      take: page.take,
      sort: sort
    }));
  }, [props]);
  
  //Formating Cells
  const OccurredCell = (props: any) => {
    return (
      <td>
        <div>{moment(props.dataItem.timestamp).format('DD/MM/YYYY @ HH:mm:ss')}</div>
      </td>
      );
  }

  const MinFlowRateCell = (props: any) => {
    return (
      <td>
        <div>{(props.dataItem.minFlowRate).toFixed(2)}</div>
      </td>
      );
  }

  const MaxFlowRateCell = (props: any) => {
    return (
      <td>
        <div>{(props.dataItem.maxFlowRate).toFixed(2)}</div>
      </td>
      );
  }

  const AvgFlowRateCell = (props: any) => {
    return (
      <td>
        <div>{(props.dataItem.avgFlowRate).toFixed(2)}</div>
      </td>
      );
  }

  const VolumeCell = (props: any) => {
    return (
      <td>
        <div>{(props.dataItem.volume).toFixed(2)}</div>
      </td>
      );
  }

  const exportData = useMemo(() => {
    return props.events.map((item) => ({
      ...item,
      timestampExport: moment(item.timestamp).format('DD/MM/YYYY HH:mm:ss'),
      
    }));
  }, [props.events]);

  const getExcelExportData = () => {
    return process(exportData, {filter: dataState.dataState.filter, sort: dataState.dataState.sort}).data;
  };
  
  return (
    <div>
      <ExcelExport data={getExcelExportData()} ref={_export} collapsible={true} fileName={`Flow Rate Events.xlsx`}>
        <ExcelExportColumn field="timestampExport" title={`${localizationService.toLanguageString('custom.occurred', enMessages.custom.occurred)}`} cellOptions={{ format: "dd/mm/yyyy hh:mm:ss" }} width={150}/>
        <ExcelExportColumn field="serialNo" title={`${localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)}`} /> 
        <ExcelExportColumn field="minFlowRate" title={`${localizationService.toLanguageString('custom.minFlowRate', enMessages.custom.minFlowRate)}`}  cellOptions={{ format: "0.00" }} />
        <ExcelExportColumn field="avgFlowRate" title={`${localizationService.toLanguageString('custom.avgFlowRate', enMessages.custom.avgFlowRate)}`} cellOptions={{ format: "0.00" }}/>   
        <ExcelExportColumn field="maxFlowRate" title={`${localizationService.toLanguageString('custom.maxFlowRate', enMessages.custom.maxFlowRate)}`} cellOptions={{ format: "0.00" }}/>
        <ExcelExportColumn field="windowSize" title={`${localizationService.toLanguageString('custom.windowSize', enMessages.custom.windowSize)}`}/>
        <ExcelExportColumn field="samplePeriod" title={`${localizationService.toLanguageString('custom.samplePeriod', enMessages.custom.samplePeriod)}`}/>
        <ExcelExportColumn field="volume" title={`${localizationService.toLanguageString('custom.volumeInL', enMessages.custom.volumeInL)})`} cellOptions={{ format: "0.00" }}/>
      </ExcelExport>
      <Grid
        key={props.events.length.toString()}
        rowHeight={46}
        take={page.take}
        data={dataState.result}
        scrollable={"virtual"}
        filterable={true}
        onDataStateChange={dataStateChange}
        {...dataState.dataState}
        sortable={true}
        style={{
          height: `600px`,
        }}
        className=" w-full z-0 extra-filter-button "
      >
        <GridToolbar>  
        <div style={{width:'100%', display:'flex', justifyContent:'space-between'}}>
            <div className="gb-h3 pl-1">{localizationService.toLanguageString('custom.events', enMessages.custom.events)}</div>
              <Tooltip anchorElement="target" position="bottom">
                <Button
                  title={localizationService.toLanguageString('custom.exportToExcel', enMessages.custom.exportToExcel)}
                  svgIcon={downloadIcon}
                  disabled={dataState.result.data.length === 0}
                  onClick={excelExport}
                />
              </Tooltip>
          </div>
        </GridToolbar>
        <Column field="timestamp" title={`${localizationService.toLanguageString('custom.occurred', enMessages.custom.occurred)}`} cell={OccurredCell} filterable={false}/>
        <Column field="serialNo" title={`${localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)}`}  filterCell={CustomTextFilterCell}/>
        <Column field="minFlowRate" title={`${localizationService.toLanguageString('custom.minFlowRate', enMessages.custom.minFlowRate)}`} filterCell={CustomTextFilterCell} cell={MinFlowRateCell}/>
        <Column field="avgFlowRate" title={`${localizationService.toLanguageString('custom.avgFlowRate', enMessages.custom.avgFlowRate)}`} filterCell={CustomTextFilterCell} cell={AvgFlowRateCell} />   
        <Column field="maxFlowRate" title={`${localizationService.toLanguageString('custom.maxFlowRate', enMessages.custom.maxFlowRate)}`} filterCell={CustomTextFilterCell} cell={MaxFlowRateCell}/>
        <Column field="windowSize" title={`${localizationService.toLanguageString('custom.windowSize', enMessages.custom.windowSize)}`} filterCell={CustomTextFilterCell}/>
        <Column field="samplePeriod" title={`${localizationService.toLanguageString('custom.samplePeriod', enMessages.custom.samplePeriod)}`} filterCell={CustomTextFilterCell}/>
        <Column field="volume" title={`${localizationService.toLanguageString('custom.volumeInL', enMessages.custom.volumeInL)}`} filterCell={CustomTextFilterCell} cell={VolumeCell}/>
      </Grid>     
  </div>
  );
}